import { ReactNode } from 'react';
import { BriefingGrade, BriefingItemDto } from '../../types/opsBriefing';
import { GRADE_LABEL } from './mockData';
import OpsDetailModal from './OpsDetailModal';

/**
 * 브리핑 항목 상세 — 화면 한가운데 뜨는 팝업.
 *
 * 회원 상세(MemberDetailDrawer)·강사 데이터(CoachDataDrawer)와 **같은 관용구**입니다 —
 * 어두운 막 + 가운데 팝업 + 네이비 머리말. 껍데기는 세 화면이 `OpsDetailModal`
 * 하나를 같이 씁니다. 세 화면이 다르게 열리면 원장님은 화면마다 다른 조작을 배워야 합니다.
 *
 * (예전에는 오른쪽에서 밀려 나오는 서랍이었습니다. 왜 가운데로 옮겼는지는
 *  OpsDetailModal 첫머리에 적어 두었습니다.)
 *
 * ⚠️ 이 파일은 **머리말만** 맡습니다. 안에 들어가는 내용(근거·추천 문구·지시 버튼)은
 *    원래 쓰던 BriefingCard 를 그대로 children 으로 넣습니다.
 *    상세를 여기서 다시 그리면 목록을 열었을 때와 안 열었을 때 화면이 갈립니다.
 *
 * 회원 상세보다 넓습니다(880px). BriefingCard 는 좌측 체크칸 66px + 본문 +
 * 우측 지표 패널 268px 인 3단 구조라, 720px 에 넣으면 본문이 350px 로 눌려
 * 추천 문구 초안이 여섯 줄로 흐릅니다.
 */

const GRADE_ACCENT: Record<BriefingGrade, string> = {
    DANGER: '#FF8A92',
    WARNING: '#F5D08A',
    CHANCE: '#9DC2FF',
    PRAISE: '#8FE3CD',
};

type Props = {
    item: BriefingItemDto;
    onClose: () => void;
    /**
     * Esc 를 이 팝업이 먹으면 안 되는 순간.
     *
     * 지시 확인 창이 이 위에 떠 있을 때 Esc 를 누르면 **확인 창만** 닫혀야 합니다.
     * 둘 다 닫히면 원장님은 취소를 눌렀을 뿐인데 보던 항목까지 잃습니다.
     * (MemberDetailDrawer 와 같은 규칙입니다)
     */
    escapeLocked?: boolean;
    children: ReactNode;
};

const BriefingDetailDrawer = ({ item, onClose, escapeLocked = false, children }: Props) => {
    const suffix = item.subjectType === 'MEMBER' ? ' 회원' : item.subjectType === 'COACH' ? ' 강사' : '';

    return (
        <OpsDetailModal
            label={`${item.subjectName}${suffix} 시그널 상세`}
            closeLabel="시그널 상세 닫기"
            width={880}
            onClose={onClose}
            escapeLocked={escapeLocked}
            header={
                <div className="flex min-w-0 flex-col">
                    <div className="flex items-center" style={{ gap: 8 }}>
                        <span
                            className="rounded-[5px]"
                            style={{
                                background: 'rgba(255,255,255,0.16)',
                                color: GRADE_ACCENT[item.grade],
                                fontSize: 11.5,
                                fontWeight: 800,
                                padding: '3px 8px',
                                flex: 'none',
                            }}
                        >
                            {GRADE_LABEL[item.grade]}
                        </span>
                        <span
                            style={{
                                fontSize: 20,
                                fontWeight: 800,
                                color: '#FFFFFF',
                                letterSpacing: '-0.02em',
                            }}
                        >
                            {item.subjectName}
                            {suffix}
                        </span>
                    </div>
                    <span style={{ fontSize: 13, color: '#9DB4D8', marginTop: 5 }}>
                        {/* 담당 강사가 없는 건은 없는 대로 말한다 — 빈칸을 남기지 않는다 */}
                        {item.relatedCoachName ? `${item.relatedCoachName} 강사 담당 · ` : ''}
                        {item.title}
                    </span>
                </div>
            }
        >
            {/* 본문 — 목록이 쓰던 브리핑 카드 그대로 */}
            {children}
        </OpsDetailModal>
    );
};

export default BriefingDetailDrawer;
